import React from 'react';
import { renderStatusText } from '../CombatPlayerbar/statusWordGlyphs';
import './KeystoneBeatAnimation.css';

/**
 * KeystoneBeatAnimation — a faithful miniature of the real guideline-MCQ combat
 * card, shrunk into the keystone tooltip's "How the beats work" section.
 *
 * The beat plays as a continuous gentle loop while the tooltip is open: neutral
 * -> option B selected (white) -> B reveal-correct (green) + A dims + check draws
 * -> outcome ping rises (+10 HP "Good Outcome") -> a readable HOLD on the answered
 * frame -> a soft reset back to neutral -> repeat. One shared CSS clock keeps the
 * badge/options/check/outcome in lockstep every cycle; ease-in-out, no springs.
 *
 * MECHANIC (verified against the engine 2026-06-26): a correct guideline beat
 * deals NO boss damage and builds NO lactic. It lands a flat +10 HP "Good Outcome"
 * heal (batch_combat_engine good_outcome_*). So the outcome shows ONLY +10 HP --
 * the lactic story belongs to the separate Heal action (see KeystoneHealAnimation).
 *
 * Tokens are lifted 1:1 from QuizSession.css / MCQOptions.jsx; the outcome ping
 * is painted by the real renderStatusText engine so "+10 HP" gets the real
 * HeartGlyph (rgb 74,222,128).
 *
 * Pure CSS/SVG; no canvas. Accent flows from var(--pill-accent) (keystone-adaptive).
 * Reduced-motion: the loop stops and the base CSS (which paints the answered frame:
 * B green, badge inverted, check drawn, A dimmed, ping visible) remains the static
 * resting state — see the @media (prefers-reduced-motion: reduce) block in the CSS.
 */
export default function KeystoneBeatAnimation() {
  return (
    <div
      className="kba"
      role="img"
      aria-label="Mini guideline question. Option B is correct, landing a Good Outcome: a flat +10 HP heal."
    >
      <div className="kba__stem">Guideline-directed first-line agent?</div>

      <div className="kba__options">
        {/* Option A — distractor that dims at reveal */}
        <div className="kba__opt kba__opt--a">
          <span className="kba__badge">A</span>
          <span className="kba__text">Sulfonylurea</span>
        </div>
        {/* Option B — selected then revealed correct */}
        <div className="kba__opt kba__opt--b">
          <span className="kba__badge">B</span>
          <span className="kba__text">Metformin</span>
          <span className="kba__check" aria-hidden="true">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path className="kba__check-path" d="M20 6L9 17L4 12"
                    stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>

      {/* Outcome — the "Good Outcome": a flat +10 HP heal, rendered as a faithful
          MINIATURE of the real in-combat Good Outcome result pill
          (.batch-result-pill--good-outcome): metformin-teal accent stripe + gradient
          + inset ring + uppercase label, scaled down for the tooltip surface. The
          value is the REAL renderStatusText status pill (HeartGlyph) sitting INSIDE
          the chip, exactly as the combat pill carries its +N value. NO lactic pip:
          a correct beat builds none (that is the Heal action's mechanic).
          aria-hidden because the wrapper role=img aria-label already states it. */}
      <div className="kba__outcome" aria-hidden="true">
        <span className="kba__go-pill">
          <span className="kba__go-tag">Good Outcome</span>
          <span className="kba__pip kba__pip--hp">{renderStatusText('+10 HP', { iconSize: 11 })}</span>
        </span>
      </div>
    </div>
  );
}
